import React, { PureComponent, Fragment } from 'react'
import i18n from 'i18next'
import * as R from 'ramda'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import { cloneDeep } from 'lodash'
import {
    getQuestionTitle, isSingleAxis, isDoubleAxis, getOptionTitle,
} from '../../../../../utils/QuestionHelper'
import { unescapeHtmlTags, sortedIndexArray } from '../../../../../utils/helpers'
import Button from '../../../../../UniformComponents/Button'
import Chart from '../Chart'
import Anchor from '../../../../../UniformComponents/Anchor'
import AiOverview from './AiOverview'

@observer
class XTab extends PureComponent {
    cols = []

    constructor(props) {
        super(props)

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                const stateCopy = { ...this.state }
                stateCopy.isLoading = true
                this.setState(stateCopy, () => {
                    this.getReportData()
                })
            },
        )

        props.reportStore.reportData.questions[props.xTabId] = {}

        this.state = {
            cols: [],
            rows: [],
            isLoading: true,
            sorting: {
                direction: undefined,
                sortBy: undefined,
                sortedIndexArray: [],
            },
        }
    }

    componentDidMount() {
        const { scrollBottom } = this.props
        console.log('xtab componenDidMount')
        console.log('XTabs scrollBottom')
        scrollBottom()
        this.getReportData()
    }

    getChartCategories = () => {
        const { sorting, cols, rows } = this.state
        const { sortedIndexArray } = sorting
        let categories
        if (this.singleAxisChart) {
            categories = sortedIndexArray.map((sortedIndex) => (
                rows[sortedIndex].LANGVALUE
            ))
        } else if (this.doubleAxisChart) {
            categories = rows.map(({ LANGVALUE }) => (
                LANGVALUE
            ))
        } else {
            categories = cols.map(({ LANGVALUE }) => (
                LANGVALUE
            ))
        }
        return categories
    }

    getReportData = async () => {
        const { xTabId, reportStore } = this.props

        const sorting = cloneDeep(this.state.sorting)
        console.log('xtab getReportData')

        try {
            const [rowQuestionId, colQuestionId] = xTabId.split('_')
            const rowQuestion = reportStore.pageQuestionMap.get(rowQuestionId.toUpperCase())
            const colQuestion = reportStore.pageQuestionMap.get(colQuestionId.toUpperCase())

            this.hasNumericCodes = (
                rowQuestion.questionTypeCode === 'RT'
                || R.pathOr(false, ['optionSetX', 'hasNumericCodes'], rowQuestion)
                || R.pathOr(false, ['optionSet', 'hasNumericCodes'], rowQuestion)
            )

            this.stackedChart = !this.hasNumericCodes && (colQuestion.questionTypeCode === 'TB' || rowQuestion.questionTypeCode === 'TB')
            this.singleAxisChart = !this.hasNumericCodes && !this.stackedChart
            this.doubleAxisChart = rowQuestion.questionTypeCode === 'TB'

            const data = await fetch(`/survey/${reportStore.surveyId}/getreportdata/?method=dspXTAB&pagequestionId=${xTabId}&${reportStore.toQueryString()}`)
            const jsonData = await data.json()

            if (!sorting.defaultSorting) {
                sorting.defaultSorting = Array.from(Array(jsonData.RESULTS.length).keys())
                sorting.sortedIndexArray = Array.from(Array(jsonData.RESULTS.length).keys())
            }

            let xOptions
            let yOptions

            jsonData.XRESULTS = jsonData.XRESULTS.map((r) => {
                r.LANGVALUE = unescapeHtmlTags(r.LANGVALUE)
                return r
            })

            jsonData.RESULTS = jsonData.RESULTS.map((r) => {
                r.LANGVALUE = unescapeHtmlTags(r.LANGVALUE)
                return r
            })

            if (!this.singleAxisChart && !this.doubleAxisChart) {
                xOptions = jsonData.XRESULTS.reduce((obj, { }, index) => {
                    jsonData.RESULTS.forEach(({ QUESTIONOPTIONID, RESULTS }) => {
                        const xId = QUESTIONOPTIONID.toUpperCase()
                        if (!R.hasPath([xId], obj)) {
                            obj[xId] = {
                                numberOfResponses: [],
                                pctOfResponses: [],
                            }
                        }

                        const { COUNT, PCT } = RESULTS[index]
                        obj[xId].numberOfResponses.push(COUNT)
                        obj[xId].pctOfResponses.push(PCT)
                    })
                    return obj
                }, {})
                yOptions = jsonData.XRESULTS.map(({ LANGVALUE }) => LANGVALUE)
            } else {
                xOptions = jsonData.RESULTS.reduce((obj, { RESULTS }) => {
                    RESULTS.forEach(({ COUNT, PCT }, index) => {
                        const xId = jsonData.XRESULTS[index].QUESTIONOPTIONID.toUpperCase()
                        if (!R.hasPath([xId], obj)) {
                            obj[xId] = {
                                numberOfResponses: [],
                                pctOfResponses: [],
                            }
                        }

                        obj[xId].numberOfResponses.push(COUNT)
                        obj[xId].pctOfResponses.push(PCT)
                    })
                    return obj
                }, {})
                yOptions = jsonData.RESULTS.map(({ LANGVALUE }) => LANGVALUE)
            }

            this.chartSortedIndexArray = Array
                .from(Array(jsonData.XRESULTS.length).keys())

            const model = {
                yOptions,
                xOptions,
                average: this.hasNumericCodes
                    ? jsonData.XRESULTS.map(({ MEAN }) => MEAN)
                    : [],
            }

            this.chartType = model.yOptions.length > 10
                ? 'bar'
                : 'column'

            this.cols = []

            if (this.hasNumericCodes) {
                this.cols.push({
                    label: 'Average',
                    id: 'Average',
                    path: 'average',
                    type: 'Average',
                    colorKey: 'survey',
                })
            } else {
                const data = (this.singleAxisChart || this.doubleAxisChart)
                    ? jsonData.XRESULTS
                    : jsonData.RESULTS

                this.cols = data.map(({ QUESTIONOPTIONID, LANGVALUE }) => ({
                    name: LANGVALUE,
                    id: QUESTIONOPTIONID.toUpperCase(),
                }))

                if (this.singleAxisChart || !this.stackedChart) {
                    this.cols = this.cols.map((cols) => {
                        cols.path = `xOptions.${cols.id}.pctOfResponses`
                        cols.type = 'OptionResponse'
                        cols.label = cols.name
                        Reflect.deleteProperty(cols, 'name')
                        return cols
                    })
                }
            }

            reportStore.reportData.questions[xTabId] = model

            this.showChart = !(colQuestion.questionTypeCode === 'TB' || rowQuestion.questionTypeCode === 'TB')

            this.setState({
                cols: jsonData.XRESULTS,
                rows: jsonData.RESULTS,
                isLoading: false,
                mean: Boolean(jsonData.MEAN),
                nps: jsonData.XRESULTS.some(({ NPS }) => !R.isNil(NPS)),
                sorting,
            })
        } catch (e) {
            console.log(`Error getting getReportData -> ${e}`)
        }
    }

    optionTitle = (option, question) => {
        const { reportStore, store } = this.props
        const { survey } = store
        console.log(`optionTitle ${JSON.stringify(option, null, 4)}`)
        return getOptionTitle({
            langs: option.langs,
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        })
    }

    colValue = (id, question) => {
        const { reportStore } = this.props
        let value = 'not found'
        if (isSingleAxis(question)) {
            const option = question.optionSet.options.find((o) => o.id.toUpperCase() === id.toUpperCase())
            value = this.optionTitle(option, question)
        } else if (isDoubleAxis(question)) {
            const [yId, xId] = id.split('_')
            const xOption = question.optionSetX.options.find((o) => o.id.toUpperCase() === xId.toUpperCase())
            const yOption = question.optionSetY.options.find((o) => o.id.toUpperCase() === yId.toUpperCase())
            if (!R.isNil(xOption) && !R.isNil(yOption)) {
                value = `${this.optionTitle(yOption, question)} - ${this.optionTitle(xOption, question)}`
            }
        }
        return value
    }

    sortIcon = (index) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                index === undefined && sortBy === 'label'
                || index === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    sortBy = (index) => {
        const sorting = R.clone(this.state.sorting)
        const { reportStore, xTabId, store } = this.props
        const { rows } = this.state
        const { survey } = store
        const data = rows

        const [rowQuestionId, colQuestionId] = xTabId.split('_')
        const rowQuestion = reportStore.pageQuestionMap.get(rowQuestionId.toUpperCase())

        // [index].RESULTS
        console.log(`sortBy colIndex ${JSON.stringify(index)}`)

        const oldSortBy = sorting.sortBy

        let values = []
        if (R.isNil(index)) {
            sorting.sortBy = 'label'
            values = rows.map((row, rowIndex) => this.colValue(row.QUESTIONOPTIONID, rowQuestion))
        } else if (index === 'count') {
            sorting.sortBy = 'count'
            values = rows.map((row) => row.COUNT)
        } else {
            sorting.sortBy = index
            values = rows.map((row) => row.RESULTS[index].PCT)
        }
        const indexArray = sortedIndexArray(values)

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = indexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = indexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }
        console.log(`sorting (${sorting.defaultSorting}) -> ${JSON.stringify(sorting, null, 4)}`)
        this.setState({
            sorting,
        })
    }

    render() {
        const {
            reportStore, store, xTabId, scrollTop,
        } = this.props
        const { survey } = store
        const {
            cols, rows, mean, sorting, nps,
        } = this.state
        const [rowQuestionId, colQuestionId] = xTabId.split('_')
        const data = reportStore.reportData.questions[xTabId]
        const colQuestion = reportStore.pageQuestionMap.get(colQuestionId.toUpperCase())
        const rowQuestion = reportStore.pageQuestionMap.get(rowQuestionId.toUpperCase())
        
        return (
            <Fragment>
                <Button cssClass="xtabremove" style={{ width: '100%', marginBottom: '10px' }} id={xTabId} onClick={reportStore.removeXTab}>{i18n.t('report_remove_xtab')}</Button>
                <div className="panel filter" style={{ position: 'relative' }}>
                    <div id={`questionData_${xTabId}`}>
                        <h2 className="reportquestiontitle">
                            <i className="far fa-th" style={{ marginRight: '10px' }} />
                            <span>
                                {
                                    getQuestionTitle({
                                        content: rowQuestion.content.shortTitle,
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: rowQuestion.metadata.initialLocale,
                                    })
                                }
                                {' '}
                                VS
                                {
                                    getQuestionTitle({
                                        content: colQuestion.content.shortTitle,
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: colQuestion.metadata.initialLocale,
                                    })
                                }
                            </span>
                        </h2>
                        {this.state.isLoading && <div className="overlay" style={{ position: 'absolute', borderRadius: '5px' }}><div className="loader" /></div>}

                        <div style={{ maxWidth: '100%', overflowY: 'auto' }}>
                            <table className="datatable question tablesorter tablesorter-default">
                                <thead>
                                    <tr>
                                        <th
                                            onClick={() => this.sortBy()}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {i18n.t('report_option_name')}
                                            {' '}
                                            {this.sortIcon()}
                                        </th>
                                        <th
                                            onClick={() => this.sortBy('count')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Count
                                            {' '}
                                            {this.sortIcon('count')}
                                        </th>
                                        {cols.map((col, index) => (
                                            <th
                                                key={col.QUESTIONOPTIONID}
                                                onClick={() => this.sortBy(index)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {this.colValue(col.QUESTIONOPTIONID, colQuestion)}
                                                {' '}
                                                (
                                                {col.COUNT}
                                                )
                                                {this.sortIcon(index)}
                                            </th>
                                        ))}
                                        <td className="tlast" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorting.sortedIndexArray.map((index) => (
                                        <tr key={rows[index].QUESTIONOPTIONID}>
                                            <th>{this.colValue(rows[index].QUESTIONOPTIONID, rowQuestion)}</th>
                                            <td>{rows[index].COUNT}</td>
                                            {rows[index].RESULTS.map((col) => (
                                                <td key={`${rows[index].QUESTIONOPTIONID}_${col.QUESTIONOPTIONID}`}>
                                                    <a value={`${rows[index].QUESTIONOPTIONID}_${col.QUESTIONOPTIONID}`}>
                                                        {col.PCT.toFixed(2)}
                                                        %
                                                    </a>
                                                </td>
                                            ))}
                                            <td className="tlast" />
                                        </tr>
                                    ))}
                                </tbody>
                                {(mean || nps) && (
                                    <tfoot>
                                        {mean && (
                                            <tr className="col">
                                                <th>
                                                    {i18n.t('report_question_average')}
                                                    :
                                                </th>
                                                <td />
                                                {cols.map((col) => (
                                                    <th className="stats">{col.MEAN.toFixed(2)}</th>
                                                ))}
                                                <td className="tlast" />
                                            </tr>
                                        )}
                                        {nps && (
                                            <tr className="col">
                                                <th>
                                                    {i18n.t('report_net_promoter_score')}
                                                    :
                                                </th>
                                                <td />
                                                {cols.map((col) => (
                                                    <th className="stats">{col.NPS.toFixed(2)}</th>
                                                ))}
                                                <td className="tlast" />
                                            </tr>
                                        )}
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                    {(!this.state.isLoading && this.showChart) && (
                        <Chart
                            stacked={this.stackedChart}
                            colors={{}}
                            dataKey={xTabId}
                            percentages={!this.hasNumericCodes}
                            survey={survey}
                            data={data}
                            series={this.cols}
                            sort={`${this.state.sorting.sortBy}_${this.state.sorting.direction}`}
                            sortedIndexArray={(this.hasNumericCodes || (this.stackedChart && !this.doubleAxisChart)) ? this.chartSortedIndexArray : this.state.sorting.sortedIndexArray}
                            isBenchmarked={false}
                            pageQuestionId={xTabId}
                            id={xTabId}
                            chartType={this.chartType}
                            categorySet={this.doubleAxisChart ? this.state.rows : this.state.cols}
                            questionTypeCode="XTAB"
                            categories={this.getChartCategories()}
                            reportStore={reportStore}
                            overrideCategories={false}
                        />
                    )}
                </div>
            </Fragment>
        )
    }
}

export default XTab
